import Vuex from 'vuex'
import Vue from 'vue'
import VuexPersistence from 'vuex-persist'
import axios from 'axios'

const vuexLocal = new VuexPersistence({
    key: 'aloware_vuex_admin_local',
    storage: window.localStorage,
    modules: ['cache']
})

Vue.use(Vuex)

const default_state = {
    filter: {
        user: {
            status: 'all',
            hide_sub_users: true,
            show_cancelled: false,
            plan_id: null,
            rate_id: null
        },
        company: {
            status: 'all',
            show_cancelled: false,
            plan_id: null,
            rate_id: null
        }
    },
    plans: [],
}

const default_cached_state = {
    sidebar_folded: true
}

const cachedStatesModule = {
    namespaced: true,

    state: {
        sidebar_folded: true
    },

    actions: {
        setSidebarFolded({commit}, status) {
            commit('SET_SIDEBAR_FOLDED', status)
        },

        resetCache({commit}) {
            commit('RESET_CACHE')
        }
    },

    mutations: {
        SET_SIDEBAR_FOLDED(state, status) {
            state.sidebar_folded = status
        },

        RESET_CACHE(state) {
            state = Object.assign(state, default_cached_state)
        }
    }
}

export default new Vuex.Store({
    modules: {
        cache: cachedStatesModule
    },

    state: {
        filter: {
            user: {
                status: 'all',
                hide_sub_users: true,
                show_cancelled: false,
                plan_id: null,
                rate_id: null
            },
            company: {
                status: 'all',
                show_cancelled: false,
                plan_id: null,
                rate_id: null
            }
        },
        account_filters: {
            search: '',
            status: null,
            reseller_id: null,
            abandoned_type: null,
            active_accounts_no_logins_7_days: 0,
            registered_customer_profile: 0,
            registered_a2p_trust_bundle: 0,
            registered_a2p_brand: 0,
            registered_stir_shaken: 0,
            registered_voice_integrity: 0,
            registered_address: 0,
            submitted_compliance: 0,
            a2p_registration_type: null,
            lrn_lookup_enabled: null,
        },
        companies: [],
        users: [],
        admins: [],
        plans: [],
        rates: [],
        addons: [],
        loading_sync_bundle: [],
        loading_update_bundle: [],
        loading_delete_bundle: [],
        env: null,
        resellerSettings: {},
    },

    getters: {
        getFilter: state => {
            return state.filter
        },

        getAccountFilters: state => {
            return state.account_filters
        },

        getAddons: state => {
            return state.addons
        },
        
        getResellerSettings: state => (resellerId) => {
            return state.resellerSettings[resellerId] || null
        }
    },

    actions: {
        setPlans({commit}, plans) {
            commit('SET_PLANS', plans)
        },

        setRates({commit}, rates) {
            commit('SET_RATES', rates)
        },

        newCompany({commit}, company) {
            commit('NEW_COMPANY', company)
        },

        setCompanies({commit}, companies) {
            commit('SET_COMPANIES', companies)
        },

        newUser({commit}, user) {
            commit('NEW_USER', user)
        },

        setUsers({commit}, users) {
            commit('SET_USERS', users)
        },

        setAdmins({commit}, admins) {
            commit('SET_ADMINS', admins)
        },

        setSidebarFolded({commit}, status) {
            commit('SET_SIDEBAR_FOLDED', status)
        },

        resetFilters({commit}, filter_name) {
            commit('RESET_FILTERS', filter_name)
        },

        setAccountFilters({commit}, filters) {
            commit('SET_ACCOUNT_FILTERS', filters)
        },

        pushLoadingUpdateBundleId({commit}, id) {
            commit('PUSH_LOADING_UPDATE_BUNDLE_ID', id)
        },

        pullIdFromLoadingUpdateBundle({commit}, id) {
            commit('PULL_LOADING_UPDATE_BUNDLE_ID', id)
        },

        pushIdToLoadingDeleteBundle({commit}, id) {
            commit('PUSH_LOADING_DELETE_BUNDLE_ID', id)
        },

        pullIdFromLoadingDeleteBundle({commit}, id) {
            commit('PULL_LOADING_DELETE_BUNDLE_ID', id)
        },

        pushComplianceSyncType({commit}, type) {
            commit('PUSH_COMPLIANCE_SYNC_TYPE', type)
        },

        pullComplianceSyncType({commit}, type) {
            commit('PULL_COMPLIANCE_SYNC_TYPE', type)
        },

        setAddons({commit}, addons) {
            commit('SET_ADDONS', addons)
        },

        setEnv({commit}, env) {
            commit('SET_ENV', env)
        }
    },

    mutations: {
        SET_PLANS(state, plans) {
            state.plans = plans
        },

        SET_RATES(state, rates) {
            state.rates = rates
        },

        NEW_COMPANY(state, company) {
            state.companies.push(company)
        },

        SET_COMPANIES(state, companies) {
            state.companies = companies
        },

        NEW_USER(state, user) {
            state.users.push(user)
        },

        SET_USERS(state, users) {
            state.users = users
        },

        SET_ADMINS(state, admins) {
            state.admins = admins
        },

        SET_SIDEBAR_FOLDED(state, status) {
            state.sidebar_folded = status
        },

        RESET_FILTERS(state, filter_name) {
            state.filter[filter_name] = Object.assign(state.filter[filter_name], default_state.filter[filter_name])
        },

        SET_ACCOUNT_FILTERS(state, filters) {
            state.account_filters = filters
        },

        PUSH_LOADING_UPDATE_BUNDLE_ID(state, id) {
            if (id) {
                state.loading_update_bundle.push(+id)
            }
        },

        PULL_LOADING_UPDATE_BUNDLE_ID(state, id) {
            if (id) {
                const index = state.loading_update_bundle.indexOf(+id)
                if (index > -1) {
                    state.loading_update_bundle.splice(index, 1)
                }
            }
        },

        PUSH_LOADING_DELETE_BUNDLE_ID(state, id) {
            if (id) {
                state.loading_delete_bundle.push(+id)
            }
        },

        PULL_LOADING_DELETE_BUNDLE_ID(state, id) {
            if (id) {
                const index = state.loading_delete_bundle.indexOf(+id)
                if (index > -1) {
                    state.loading_delete_bundle.splice(index, 1)
                }
            }
        },

        PUSH_COMPLIANCE_SYNC_TYPE(state, type) {
            state.loading_sync_bundle.push(type)
        },

        PULL_COMPLIANCE_SYNC_TYPE(state, type) {
            const index = state.loading_sync_bundle.indexOf(type)
            if (index > -1) {
                state.loading_sync_bundle.splice(index, 1)
            }
        },

        SET_ADDONS(state, addons) {
            state.addons = addons
        },

        SET_ENV(state, env) {
            state.env = env
        },

        SET_RESELLER_SETTINGS(state, { id, settings }) {
            state.resellerSettings = {
                ...state.resellerSettings,
                [id]: settings
            }
        }
    },

    plugins: [vuexLocal.plugin]
})
